import {useState} from 'react';
import clsx from 'clsx';
import {Image, Link} from '@shopify/hydrogen';
import imageUrlBuilder from '@sanity/image-url';
import {Button} from '~/components';
import {Text} from '../elements/Text';
import {HeroOverlay} from './HeroOverlay.client';
import {TransitionVisible} from './TransitionVisible';
import {HeroEntity} from '~/types/HeroEntity';
import {SpreadMedia} from '../media/SpreadMedia';
import {Modal} from '../Modal.client';
import YoutubeEmbed from '../YoutubeEmbed.client';
import getYouTubeId from 'get-youtube-id';
import {ButtonLinkEntity} from '~/types/index';
import {twMerge} from 'tailwind-merge';
import {DATA_TEST_IDS} from '~/lib/const';
import sanityConfig from '../../../sanity.config';

const getShopifyImage = (
  url: string,
  options?: {width: string; height: string; format: string},
) => {
  const {width, height, format = 'webp'} = options ?? {};
  return `${url}w=${width}&h=${height}&format=${format}`;
};

interface HeroImageProps {
  hero: HeroEntity;
  breakpoints?: number[][];
  heroImageClassname?: string;
}

const HeroImage: React.FC<HeroImageProps> = ({
  hero,
  breakpoints,
  heroImageClassname,
}) => {
  const altText = hero.heroContentBlock?.title ?? 'skiessentials hero';

  if (hero.assetType === 'imageUrl' && hero.image?.url != null) {
    return (
      <Image
        loading="lazy"
        src={getShopifyImage(hero.image?.url)}
        width="100%"
        height="auto"
        alt={altText}
      />
    );
  }

  return (
    <SpreadMedia
      loading="eager"
      media={hero}
      alt={altText}
      breakpoints={breakpoints}
      className={heroImageClassname}
    />
  );
};

interface HeroImageWrapperProps {
  hero: HeroEntity;
}

const HeroImageWrapper: React.FC<HeroImageWrapperProps> = ({hero}) => {
  if (!hero) return null;

  const hasMobileAsset = Boolean(
    hero?.mobileAsset?.photo && Object.keys(hero.mobileAsset.photo)?.length > 0,
  );
  const hasPhoto = Boolean(
    hero?.assetType !== 'none' &&
      hero?.photo &&
      Object.keys(hero.photo)?.length > 0,
  );

  let heroTypeClassname = '';
  let heroImageClassname = '';
  let heroTypeStyles = {};
  let heroTypeBreakpoints = [
    [320, 634],
    [640, 634],
    [768, 634],
    [1024, 634],
    [1280, 634],
    [1536, 634],
    [1728, 634],
    [1920, 634],
    [2560, 634],
    [3440, 634],
    [3840, 634],
    [4026, 634],
  ];

  if (hasPhoto) {
    const urlBuilder =
      hero?.photo?.heroImage?.asset?._ref &&
      imageUrlBuilder({
        projectId: sanityConfig.projectId,
        dataset: sanityConfig.dataset,
      })
        .image({_ref: hero?.photo?.heroImage?.asset?._ref})
        .format('webp')
        .quality(80);

    if (!urlBuilder) return null;

    const urlDefault = urlBuilder?.url();

    const [originalWidth, originalHeight] = urlDefault
      .split('/')
      .filter((p) => p)
      .pop()
      .split('.')
      .slice(0, 1)
      .join('')
      .split('-')
      .pop()
      .split('x')
      .map((px) => parseInt(px));

    if (hero?.heroContentBlock?.heroStyleToggle) {
      heroTypeClassname = 'w-full max-h-[634px]';
      heroTypeStyles = {aspectRatio: 1728 / 634};
      heroTypeBreakpoints = heroTypeBreakpoints.filter(
        (breakpoint) => breakpoint[0] <= originalWidth,
      );
    } else {
      heroTypeClassname = 'w-auto mx-auto max-h-[calc(100vh-198px)]';
      heroImageClassname = 'w-auto mx-auto';
      heroTypeStyles = {aspectRatio: originalWidth / originalHeight};
      heroTypeBreakpoints = [];
    }
  }

  const renderHeroImage = () => {
    const button = hero?.heroContentBlock?.buttons?.[0];
    if (
      hero?.heroContentBlock?.buttons?.length === 1 &&
      button?.buttonClickType === 'link' &&
      button?.buttonHref
    ) {
      return (
        <Link to={button.buttonHref}>
          <HeroImage
            hero={hero}
            breakpoints={heroTypeBreakpoints}
            heroImageClassname={heroImageClassname}
          />
        </Link>
      );
    } else {
      return (
        <HeroImage
          hero={hero}
          breakpoints={heroTypeBreakpoints}
          heroImageClassname={heroImageClassname}
        />
      );
    }
  };

  const renderMobileHeroImage = () => {
    const button = hero?.heroContentBlock?.buttons?.[0];
    if (
      hero?.heroContentBlock?.buttons?.length === 1 &&
      button?.buttonClickType === 'link' &&
      button?.buttonHref
    ) {
      return (
        <Link to={button.buttonHref}>
          <SpreadMedia
            loading="eager" //@ts-ignore
            media={hero.mobileAsset}
            alt={hero.heroContentBlock?.title ?? ''}
            className="min-h-[340px] md:h-[634px]"
            breakpoints={[
              [320, 634],
              [640, 634],
              [768, 634],
              [1024, 634],
              [1280, 634],
              [1536, 634],
            ]}
          />
        </Link>
      );
    } else {
      return (
        <SpreadMedia
          loading="eager" //@ts-ignore
          media={hero.mobileAsset}
          alt={hero.heroContentBlock?.title ?? ''}
          className="min-h-[340px] md:h-[634px]"
          breakpoints={[
            [320, 634],
            [640, 634],
            [768, 634],
            [1024, 634],
            [1280, 634],
            [1536, 634],
          ]}
        />
      );
    }
  };

  return (
    <div className={clsx(heroTypeClassname)} style={heroTypeStyles}>
      <div
        className={twMerge(
          hasMobileAsset && hero.mobileAsset?.assetType !== 'copyDesktop'
            ? 'hidden md:block'
            : '',
          'h-full',
        )}
      >
        {renderHeroImage()}
      </div>
      {hasMobileAsset && (
        <div
          className={twMerge(
            hero.mobileAsset?.assetType !== 'copyDesktop' ? 'md:hidden' : '',
          )}
        >
          {renderMobileHeroImage()}
        </div>
      )}
    </div>
  );
};

interface HeroProps {
  hero: HeroEntity;
  autoHeight?: boolean;
  disableMarginY?: boolean;
}

export const Hero: React.FC<HeroProps> = ({
  hero,
  autoHeight,
  disableMarginY = false,
}) => {
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [buttonAction, setButtonAction] = useState<
    Partial<ButtonLinkEntity> | undefined
  >(undefined);

  const handleClick = (action: Partial<ButtonLinkEntity>) => {
    if (action.buttonAction === 'scroll-section') {
      if (action.buttonActionScrollSection) {
        document
          .getElementById(action.buttonActionScrollSection)
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
      }
    } else if (
      action.buttonAction === 'modal-youtube' &&
      action.buttonActionModalYoutube
    ) {
      setButtonAction(action);
    }
  };

  const withoutMedia = hero.assetType === 'none';

  return (
    <div
      data-testid={DATA_TEST_IDS.HERO}
      className={clsx(
        'relative overflow-hidden',
        !disableMarginY && 'mb-[30px] md:mb-[45px]',
        withoutMedia ? '' : autoHeight ? 'h-auto' : '',
      )}
    >
      <HeroImageWrapper hero={hero} />

      <TransitionVisible show={!overlayVisible}>
        <div
          className={clsx(
            'flex flex-col items-center left-0 right-0 z-[1px] text-center',
            (hero.heroContentBlock?.buttons?.length ?? 0) === 0
              ? 'bottom-[100px]'
              : 'bottom-[10px]',
            withoutMedia ? 'md:bottom-[113px]' : 'sm:bottom-[100px]',
            {absolute: !(hero.heroContentBlock?.positionRelative ?? false)},
          )}
        >
          {(hero.heroContentBlock?.title ||
            hero.heroContentBlock?.description) && (
            <div
              className={clsx('mt-5 w-full px-5 rounded-lg', {
                'text-white': hero.heroContentBlock?.textColor === 'white',
                'text-black': hero.heroContentBlock?.textColor === 'black',
                'pb-2': !!hero.heroContentBlock?.description,
                'backdrop-blur-md':
                  (hero.heroContentBlock.backgroundOpacity ?? 0) > 0,
              })}
              style={{
                backgroundColor:
                  hero.heroContentBlock?.backgroundColor === 'white'
                    ? `rgba(255,255,255,${
                        (hero.heroContentBlock.backgroundOpacity ?? 0) / 100
                      })`
                    : hero.heroContentBlock?.backgroundColor === 'black'
                    ? `rgba(0,0,0,${
                        (hero.heroContentBlock.backgroundOpacity ?? 0) / 100
                      })`
                    : 'transparent',
              }}
            >
              {hero.heroContentBlock?.title && (
                <Text
                  testid={DATA_TEST_IDS.HERO_TITLE}
                  as={hero.heroContentBlock?.titleType ?? 'h1'}
                >
                  {hero.heroContentBlock?.title.toUpperCase()}
                </Text>
              )}

              {hero.heroContentBlock?.description && (
                <div
                  className={clsx(
                    'mt-[11px] line-clamp-2 px-5 md:px-0 leading-[18px]',
                    withoutMedia ? 'md:mt-4' : 'md:mt-5',
                  )}
                >
                  <Text
                    testid={DATA_TEST_IDS.HERO_DESCRIPTION}
                    className="md:leading-[21px]"
                  >
                    {hero.heroContentBlock?.description}
                  </Text>
                </div>
              )}
            </div>
          )}
          <div
            className={clsx(
              'gap-3 md:gap-[30px] flex flex-wrap justify-center mt-[30px] md:mt-9 px-5 md:px-0',
              hero.heroContentBlock?.buttonMarginSmall && 'mt-[10px] md:mt-3',
            )}
          >
            {hero.heroContentBlock?.buttons?.map((button: any) => {
              if (!button.buttonText) {
                return null;
              }

              return (
                <Button
                  data-testid={DATA_TEST_IDS.BUTTON('hero')}
                  to={button.buttonHref}
                  key={button.buttonText}
                  variant={button.buttonType}
                  width={button.buttonWidth ?? 190}
                  className="truncate"
                  onPress={handleClick}
                  entity={button}
                >
                  {button.buttonText.toUpperCase()}
                </Button>
              );
            })}
          </div>
        </div>
      </TransitionVisible>

      {hero.heroOverlay && hero.heroOverlay.heroOverlayDisplay && (
        <HeroOverlay
          title={hero.heroOverlay.copy}
          visible={overlayVisible}
          type="hero"
          setVisible={setOverlayVisible}
        />
      )}

      <Modal
        classNamePanel="max-w-4xl p-5"
        isOpen={!!buttonAction}
        onCloseModal={() => setButtonAction(undefined)}
        closeButtonSize="small"
      >
        {buttonAction &&
          buttonAction.buttonAction === 'modal-youtube' &&
          buttonAction.buttonActionModalYoutube && (
            <YoutubeEmbed
              title=""
              muted
              defaultIframe={true}
              id={getYouTubeId(buttonAction.buttonActionModalYoutube) ?? ''}
            />
          )}
      </Modal>
    </div>
  );
};
